import { useTranslation } from 'react-i18next';
import Center from 'raydiant-elements/layout/Center';
import CircularProgress from 'raydiant-elements/core/CircularProgress';
import classNames from 'classnames';
import { V4ReportingDeviceResponse } from '@raydiant/api-client-js';
import InfiniteScroll from 'react-infinite-scroll-component';
import { useEffect, useState } from 'react';

import SelectOption from '../../../components/Select/SelectOption';
import Select from '../../../components/Select/Select';

type DeviceSelectProps = Omit<JSX.IntrinsicElements['select'], 'onChange'> & {
  onChange: (value: JSX.IntrinsicElements['select']['value']) => void;
  wrapperClassName?: string;
  data: V4ReportingDeviceResponse[] | undefined;
  isLoading: boolean;
  isError: boolean;
};

const DeviceSelect = ({
  onChange,
  data,
  isLoading,
  isError,
  ...props
}: DeviceSelectProps) => {
  const { t } = useTranslation();
  const [itemsForDisplay, setItemsForDisplay] = useState<
    V4ReportingDeviceResponse[]
  >([]);
  const [isSelectorClosed, setIsSelectorClosed] = useState(true);
  const [hasMoreData, setHasMoreData] = useState<boolean>(true);
  const [page, setPage] = useState(1);
  const itemsPerPage = 50;

  useEffect(() => {
    if (data) {
      setItemsForDisplay(data.slice(0, itemsPerPage));
    }
  }, [data]);

  useEffect(() => {
    if (isSelectorClosed && page !== 1 && data) {
      setItemsForDisplay(data.slice(0, itemsPerPage));
      setPage(1);
      setHasMoreData(true);
    }
  }, [isSelectorClosed, page, data]);

  const fetchMoreData = () => {
    if (data === undefined) return;
    if (isSelectorClosed) return;
    if (itemsForDisplay.length >= data.length) {
      setHasMoreData(false);
      return;
    }
    setItemsForDisplay((prevItems) => [
      ...prevItems,
      ...data.slice(page * itemsPerPage, (page + 1) * itemsPerPage),
    ]);
    setPage(page + 1);
  };

  const getPlaceholder = data?.find((item) => item.id === props.value);

  return (
    <Select
      className={props.className}
      label={t('analytics.device')!}
      onChange={(value) => onChange(value)}
      wrapperClassName={classNames('no-label', props.wrapperClassName)}
      setIsSelectorClosed={setIsSelectorClosed}
      itemsClassName="bg-gray-100 mt-2 p-2 sm:p-0 sm:mt-0 sm:bg-white max-h-fit"
      renderAnchorElement={() => (
        <div className="w-[90%] text-ellipsis overflow-hidden whitespace-nowrap">
          {getPlaceholder?.name ?? t('analytics.allDevices')}
        </div>
      )}
      disableMobile
      {...props}
    >
      <SelectOption key="" value="" onClick={() => onChange('')}>
        {t('analytics.allDevices')}
      </SelectOption>
      {isLoading && !isError && (
        <Center className="h-20">
          <CircularProgress size={30}></CircularProgress>
        </Center>
      )}
      {isError && (
        <div className="text-center text-red-600 py-3">
          {t('analytics.allDevicesError')}
        </div>
      )}
      {!isLoading && !isError && data && (
        <InfiniteScroll
          dataLength={itemsForDisplay.length}
          next={fetchMoreData}
          hasMore={hasMoreData}
          height={400}
          loader={undefined}
        >
          {itemsForDisplay.map((item, key) => (
            <SelectOption
              key={key}
              onClick={() => onChange(item.id)}
              value={item.id}
            >
              {item.name}
            </SelectOption>
          ))}
        </InfiniteScroll>
      )}
    </Select>
  );
};

export default DeviceSelect;
